import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@radix-ui/react-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/modal";
import { SuccessIcon } from "./assets";

interface NameFormProps {
  firstName: string;
  lastName: string;
  onChange: (firstName: string, lastName: string) => Promise<boolean>;
}

export function NameForm({
  firstName: initialFirstName = "",
  lastName: initialLastName = "",
  onChange,
}: NameFormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);

  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
  }, [initialFirstName, initialLastName]);

  const handleCancel = () => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
  };

  const handleChange = async (firstName: string, lastName: string) => {
    setIsLoading(true);
    const result = await onChange(firstName, lastName);
    setIsLoading(false);
    if (result) {
      setIsSuccess(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsSuccess(false);
    }
  };

  const firstNameChange = firstName !== initialFirstName;
  const lastNameChange = lastName !== initialLastName;
  const hasChanges = firstNameChange || lastNameChange;
  return (
    <div className="flex items-end gap-3">
      <div className="w-full max-w-[268px] space-y-3">
        <Label className="text-sm font-medium text-i-normal">
          {t("settings_account.name_form.first_name")}
        </Label>
        <Input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              !hasChanges ||
                isLoading ||
                isSuccess ||
                handleChange(firstName, lastName);
            }
          }}
          className="px-3.5 py-2.5 text-i-normal placeholder:text-i-sub rounded-sm focus-visible:ring-0 focus-visible:ring-offset-0 border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] focus:border-i-primary shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
          placeholder={t("settings_account.name_form.first_name")}
        />
      </div>
      <div className="w-full max-w-[268px] space-y-3">
        <Label className="text-sm font-medium text-i-normal">
          {t("settings_account.name_form.last_name")}
        </Label>
        <Input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              !hasChanges ||
                isLoading ||
                isSuccess ||
                handleChange(firstName, lastName);
            }
          }}
          className="px-3.5 py-2.5 text-i-normal placeholder:text-i-sub rounded-sm focus-visible:ring-0 focus-visible:ring-offset-0 border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] focus:border-i-primary shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
          placeholder={t("settings_account.name_form.last_name")}
        />
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button
          disabled={!isSuccess && !hasChanges}
          variant="outline"
          onClick={() =>
            !hasChanges || isLoading || isSuccess || handleCancel()
          }
          className="px-4 py-3 w-20 text-sm font-semibold text-i-default border border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] bg-i-white hover:bg-i-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] rounded-sm"
        >
          {t("settings_account.name_form.cancel")}
        </Button>
        <Button
          disabled={!isSuccess && !hasChanges}
          onClick={() =>
            !hasChanges || isLoading || isSuccess || handleChange(firstName, lastName)
          }
          className="px-4 py-3 w-20 text-sm font-semibold text-i-white rounded-sm bg-[rgba(117,66,247,1)] hover:bg-[rgba(94,53,198,1)] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
        >
          {isLoading ? (
            <Spinner />
          ) : isSuccess ? (
            <SuccessIcon className="w-5 h-5" />
          ) : (
            t("settings_account.name_form.save")
          )}
        </Button>
      </div>
    </div>
  );
}